import type { YearValue } from "@/lib/data/types";

export function median(values: number[]): number | undefined {
  if (values.length === 0) return undefined;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
}

export function average(values: number[]): number | undefined {
  if (values.length === 0) return undefined;
  return values.reduce((sum, v) => sum + v, 0) / values.length;
}

export function medianOf(series: YearValue[]): number | undefined {
  return median(series.map((v) => v.value));
}

export function averageOf(series: YearValue[]): number | undefined {
  return average(series.map((v) => v.value));
}

/** Variationskoeffizient (Stdabw./Mittelwert) - Mass fuer Streuung/Stabilitaet. */
export function coefficientOfVariation(values: number[]): number | undefined {
  if (values.length < 2) return undefined;
  const mean = average(values);
  if (mean === undefined || mean === 0) return undefined;
  const variance = values.reduce((sum, v) => sum + (v - mean) ** 2, 0) / values.length;
  return Math.sqrt(variance) / Math.abs(mean);
}

/** Stichproben-Standardabweichung (Teiler n-1) - fuer kleine Peer-Gruppen (Z-Scores)
 * die uebliche Wahl, da diese selbst eine Stichprobe aus einer groesseren Branche sind. */
export function standardDeviation(values: number[]): number | undefined {
  if (values.length < 2) return undefined;
  const mean = average(values);
  if (mean === undefined) return undefined;
  const variance = values.reduce((sum, v) => sum + (v - mean) ** 2, 0) / (values.length - 1);
  return Math.sqrt(variance);
}

/**
 * CAGR ueber ein N-Jahres-Fenster. Nimmt den aeltesten verfuegbaren Datenpunkt bei/vor
 * (aktuellstes Jahr - years), statt ein exaktes Jahr zu verlangen - Historien haben
 * gelegentlich Luecken. Degradiert zu undefined statt einen Wert zu erfinden, wenn die
 * Historie nicht weit genug zurueckreicht.
 */
export function cagrOverYears(series: YearValue[], years: number): number | undefined {
  if (series.length < 2) return undefined;
  const sorted = [...series].sort((a, b) => a.year - b.year);
  const latest = sorted[sorted.length - 1];
  const targetYear = latest.year - years;
  const start = [...sorted].reverse().find((v) => v.year <= targetYear);
  if (!start || start.value <= 0 || latest.value <= 0 || start.year >= latest.year) {
    return undefined;
  }
  const actualYears = latest.year - start.year;
  return Math.pow(latest.value / start.value, 1 / actualYears) - 1;
}

/** Letzter N Werte einer Jahresreihe, chronologisch sortiert. */
export function lastNValues(series: YearValue[], n: number): number[] {
  return [...series].sort((a, b) => a.year - b.year).slice(-n).map((v) => v.value);
}

/**
 * Wie `cagrOverYears`, degradiert aber auf eine kuerzere Zeitspanne (bis `minYears`)
 * statt komplett auszufallen, wenn die volle `idealYears`-Historie nicht vorliegt - fuer
 * die Score-Engine, wo eine KPI moeglichst selten wegen fehlender Daten ausfallen soll
 * (siehe lib/scoring/scoring.ts). Andere Aufrufer (Dividenden-CAGR, Peer-Group-Radar)
 * nutzen bewusst weiterhin das strikte `cagrOverYears` - eine kuerzere Zeitspanne
 * dort still zu akzeptieren waere fuer diese Anwendungsfaelle nicht gewuenscht.
 * Gibt zusaetzlich die tatsaechlich genutzte Zeitspanne zurueck, damit Aufrufer das in
 * ihrer Begruendung ehrlich ausweisen koennen statt die nominale `idealYears`-Zahl.
 */
export function cagrOverYearsWithMinimum(
  series: YearValue[],
  idealYears: number,
  minYears: number,
): { cagr: number; actualYears: number } | undefined {
  if (series.length < 2) return undefined;
  const sorted = [...series].sort((a, b) => a.year - b.year);
  const latest = sorted[sorted.length - 1];
  const reversed = [...sorted].reverse();

  const idealStart = reversed.find((v) => v.year <= latest.year - idealYears);
  const start = idealStart ?? reversed.find((v) => v.year <= latest.year - minYears);

  if (!start || start.value <= 0 || latest.value <= 0 || start.year >= latest.year) {
    return undefined;
  }
  const actualYears = latest.year - start.year;
  if (actualYears < minYears) return undefined;
  return { cagr: Math.pow(latest.value / start.value, 1 / actualYears) - 1, actualYears };
}
